/** Editor для Комплектації номенклатури (Порція 4b). */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orgs, nomenclature as nomApi } from '@/api/catalogs';
import { refs as refsApi } from '@/api/refs';
import {
  productAssemblies,
  type ProductAssemblyComponentIn,
} from '@/api/productAssemblies';
import { nomenclatureSpecifications } from '@/api/nomenclatureSpecifications';
import { useNumberPreview } from '@/hooks/useNumberPreview';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import DatePopover from '@/components/DatePopover';

const PERMISSION = 'production.assembly.manage';

interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  warehouse_id: string;
  assembled_nomenclature_id: string;
  assembled_quantity: string;
  comment: string;
  components: ProductAssemblyComponentIn[];
}

const EMPTY_FORM: FormState = {
  organization_id: '',
  document_number: '',
  document_date: new Date().toISOString().slice(0, 16),
  warehouse_id: '',
  assembled_nomenclature_id: '',
  assembled_quantity: '1',
  comment: '',
  components: [],
};

const newRow = (n: number): ProductAssemblyComponentIn => ({
  line_number: n, nomenclature_id: '', quantity: '0',
});

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string } } }).response;
    return r?.data?.detail ?? 'Error';
  }
  return String(e);
}

export default function ProductAssemblyEditor() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const { availableOrgs, totalAccessibleOrgs } = usePermissionsByOrg(PERMISSION);
  const nomQ = useQuery({ queryKey: ['nomenclature-full'], queryFn: () => nomApi.list({ limit: 500 }) });
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);
  const [specId, setSpecId] = useState<string>('');

  // Активні ASSEMBLY-специфікації обраного набору — для автопідстановки складу.
  const specsQ = useQuery({
    queryKey: ['assembly-specs', form.assembled_nomenclature_id],
    queryFn: () =>
      nomenclatureSpecifications.list({
        product_nomenclature_id: form.assembled_nomenclature_id,
        purpose: 'ASSEMBLY',
        is_active: true,
        limit: 100,
      }),
    enabled: !!form.assembled_nomenclature_id,
  });
  const warehousesQ = useQuery({
    queryKey: ['warehouses', form.organization_id],
    queryFn: () => refsApi.warehouses(form.organization_id),
    enabled: !!form.organization_id,
  });
  const docQuery = useQuery({
    queryKey: ['product-assembly', id],
    queryFn: () => productAssemblies.get(id!),
    enabled: !isNew,
  });
  const numberPreview = useNumberPreview('doc.product_assemblies', form.organization_id, isNew);

  useEffect(() => {
    if (isNew && availableOrgs.length > 0 && !form.organization_id) {
      setForm((f) => ({ ...f, organization_id: availableOrgs[0]!.id }));
    }
  }, [isNew, availableOrgs, form.organization_id]);
  useEffect(() => {
    if (!isNew && docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        warehouse_id: d.warehouse_id,
        assembled_nomenclature_id: d.assembled_nomenclature_id,
        assembled_quantity: d.assembled_quantity,
        comment: d.comment ?? '',
        components: d.components.map((c) => ({
          line_number: c.line_number,
          nomenclature_id: c.nomenclature_id,
          quantity: c.quantity,
        })),
      });
    }
  }, [isNew, docQuery.data]);

  const isPosted = docQuery.data?.posted ?? false;

  const buildPayload = () => ({
    document_number: form.document_number || null,
    document_date: new Date(form.document_date).toISOString(),
    warehouse_id: form.warehouse_id,
    assembled_nomenclature_id: form.assembled_nomenclature_id,
    assembled_quantity: form.assembled_quantity,
    comment: form.comment || null,
    components: form.components,
  });

  const create = useMutation({
    mutationFn: () => productAssemblies.create({ organization_id: form.organization_id, ...buildPayload() }),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['product-assemblies'] });
      navigate(`/product-assemblies/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const update = useMutation({
    mutationFn: () => productAssemblies.update(id!, buildPayload()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['product-assembly', id] });
      qc.invalidateQueries({ queryKey: ['product-assemblies'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  // Новий: створити і одразу провести (create → post → картка).
  const createAndPost = useMutation({
    mutationFn: async () => {
      const doc = await productAssemblies.create({ organization_id: form.organization_id, ...buildPayload() });
      await productAssemblies.post(doc.id);
      return doc;
    },
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['product-assemblies'] });
      navigate(`/product-assemblies/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  // Збережений непроведений: зберегти правки і одразу провести (update → post).
  const updateAndPost = useMutation({
    mutationFn: async () => {
      await productAssemblies.update(id!, buildPayload());
      await productAssemblies.post(id!);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['product-assembly', id] });
      qc.invalidateQueries({ queryKey: ['product-assemblies'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const unpost = useMutation({
    mutationFn: () => productAssemblies.unpost(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['product-assembly', id] });
      qc.invalidateQueries({ queryKey: ['product-assemblies'] });
    },
    onError: (e) => setError(extractError(e)),
  });

  function addComponent() {
    setForm((f) => ({ ...f, components: [...f.components, newRow(f.components.length + 1)] }));
  }
  function removeComponent(i: number) {
    setForm((f) => ({
      ...f,
      components: f.components.filter((_, idx) => idx !== i).map((c, idx) => ({ ...c, line_number: idx + 1 })),
    }));
  }
  function setComponentField(i: number, patch: Partial<ProductAssemblyComponentIn>) {
    setForm((f) => ({ ...f, components: f.components.map((c, idx) => (idx === i ? { ...c, ...patch } : c)) }));
  }

  async function fillFromSpecification() {
    if (!specId) return;
    setError(null);
    try {
      const spec = await nomenclatureSpecifications.get(specId);
      setForm((f) => ({
        ...f,
        components: spec.items
          .slice()
          .sort((a, b) => a.line_number - b.line_number)
          .map((it, idx) => ({
            line_number: idx + 1,
            nomenclature_id: it.raw_material_nomenclature_id,
            quantity: it.quantity,
          })),
      }));
    } catch (e) {
      setError(extractError(e));
    }
  }

  if (!isNew && docQuery.isLoading) return <div>Завантаження…</div>;

  return (
    <div>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Нова комплектація' : `Комплектація: ${form.document_number}`}
          {isPosted && (
            <span style={{ marginLeft: '0.75rem', color: '#15803d', fontSize: '1rem' }}>
              ✓ Проведено {docQuery.data?.total_cost && `(${docQuery.data.total_cost})`}
            </span>
          )}
        </h2>
        <DocumentToolbar
          onBackToList={() => navigate('/product-assemblies')}
          onSave={() => (isNew ? create.mutate() : update.mutate())}
          saveDisabled={create.isPending || update.isPending}
          isPosted={isPosted}
          isNew={isNew}
          onPost={() => (isNew ? createAndPost.mutate() : updateAndPost.mutate())}
          postPending={create.isPending || createAndPost.isPending || updateAndPost.isPending || update.isPending}
          postTitle="Зберігає поточні зміни та проводить документ"
          onUnpost={() => unpost.mutate()}
          unpostPending={unpost.isPending}
          deletable={false}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.75rem' }}>
        <div className="field">
          <label>Юрособа</label>
          <select value={form.organization_id}
                  onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
                  disabled={!isNew}>
            <option value="">—</option>
            {(isNew ? availableOrgs : orgList.data?.items ?? []).map((o) => (
              <option key={o.id} value={o.id}>{o.code} — {o.name}</option>
            ))}
          </select>
          {isNew && availableOrgs.length < totalAccessibleOrgs && (
            <small style={{ color: '#64748b', fontSize: '0.75rem' }}>
              Показано {availableOrgs.length} з {totalAccessibleOrgs} ваших організацій
            </small>
          )}
        </div>
        <div className="field">
          <label>Номер</label>
          <input value={form.document_number}
                 onChange={(e) => setForm({ ...form, document_number: e.target.value })}
                 placeholder={isNew && numberPreview ? `буде: ${numberPreview}` : ''}
                 disabled={isPosted} />
        </div>
        <div className="field">
          <label>Дата</label>
          <DatePopover withTime value={form.document_date}
                 onChange={(v) => setForm({ ...form, document_date: v })} disabled={isPosted} />
        </div>
        <div className="field">
          <label>Склад</label>
          <select value={form.warehouse_id}
                  onChange={(e) => setForm({ ...form, warehouse_id: e.target.value })} disabled={isPosted}>
            <option value="">—</option>
            {warehousesQ.data?.map((w) => (<option key={w.id} value={w.id}>{w.code} — {w.name}</option>))}
          </select>
        </div>
        <div className="field">
          <label>Комплект (номенклатура)</label>
          <select value={form.assembled_nomenclature_id}
                  onChange={(e) => setForm({ ...form, assembled_nomenclature_id: e.target.value })}
                  disabled={isPosted}>
            <option value="">—</option>
            {nomQ.data?.items.map((n) => (<option key={n.id} value={n.id}>{n.code} — {n.name}</option>))}
          </select>
        </div>
        <div className="field">
          <label>Кількість комплекту</label>
          <input type="number" step="0.001" value={form.assembled_quantity}
                 onChange={(e) => setForm({ ...form, assembled_quantity: e.target.value })} disabled={isPosted} />
        </div>
        <div className="field" style={{ gridColumn: 'span 2' }}>
          <label>Коментар</label>
          <input value={form.comment}
                 onChange={(e) => setForm({ ...form, comment: e.target.value })} disabled={isPosted} />
        </div>
      </div>

      <div
        style={{
          marginTop: '1rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          flexWrap: 'wrap',
        }}
      >
        <span style={{ fontWeight: 600 }}>Компоненти комплекту</span>
        {!isPosted && (
          <span style={{ marginLeft: 'auto', display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
            <select
              value={specId}
              onChange={(e) => setSpecId(e.target.value)}
              disabled={!form.assembled_nomenclature_id}
              title={!form.assembled_nomenclature_id ? 'Спершу оберіть комплект' : undefined}
            >
              <option value="">— специфікація складу —</option>
              {specsQ.data?.items.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={fillFromSpecification}
              disabled={!form.assembled_nomenclature_id || !specId}
              title={
                !form.assembled_nomenclature_id
                  ? 'Спершу оберіть комплект'
                  : !specId
                    ? 'Оберіть специфікацію складу'
                    : 'Замінити компоненти складом зі специфікації'
              }
            >
              Заповнити зі специфікації
            </button>
          </span>
        )}
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
        <thead>
          <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
            <th style={{ padding: '0.5rem', width: 40 }}>#</th>
            <th style={{ padding: '0.5rem' }}>Компонент</th>
            <th style={{ padding: '0.5rem', width: 100 }}>К-сть</th>
            <th style={{ padding: '0.5rem', width: 40 }} />
          </tr>
        </thead>
        <tbody>
          {form.components.map((c, i) => (
            <tr key={i} style={{ borderTop: '1px solid #e2e8f0' }}>
              <td style={{ padding: '0.5rem' }}>{c.line_number}</td>
              <td style={{ padding: '0.5rem' }}>
                <select value={c.nomenclature_id}
                        onChange={(e) => setComponentField(i, { nomenclature_id: e.target.value })}
                        disabled={isPosted}>
                  <option value="">—</option>
                  {nomQ.data?.items.map((n) => (<option key={n.id} value={n.id}>{n.code} — {n.name}</option>))}
                </select>
              </td>
              <td style={{ padding: '0.5rem' }}>
                <input type="number" step="0.001" value={c.quantity}
                       onChange={(e) => setComponentField(i, { quantity: e.target.value })}
                       disabled={isPosted} />
              </td>
              <td style={{ padding: '0.5rem' }}>
                {!isPosted && <button onClick={() => removeComponent(i)} title="Видалити">×</button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {!isPosted && (
        <button onClick={addComponent} style={{ marginTop: '0.5rem' }}>+ Додати компонент</button>
      )}

      {error && <div className="error" style={{ marginTop: '1rem' }}>{error}</div>}
    </div>
  );
}
